import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { debounce } from 'lodash';
import { TrackerDataType } from '../../shared/constants/constants';
import { DropdownTable } from '../../shared/types';
import { getCalendarQuickSearchServices } from '../../shared/requests/serviceNetworkRequests';
import { getCalendarQuickSearchInteractions } from '../../shared/requests/interactionsRequests';
import { getCalendarQuickSearchEngagements } from '../../shared/requests/engagementsRequest';
import { getCalendarQuickSearchContentLogs } from '../../shared/requests/contentLogsRequest';
import { formatCalendarEvents } from './calendarEventUtils';
import { ALL_FIELDS_SEARCH_CATEGORY, searchCategoryByViewAndLabel } from './calendarConfig';
import { CalendarTopBarView } from './CalendarCategoryBar';
import { CalendarEvent } from './types';

const RESULT_CAP = 20;
const DEBOUNCE_MS = 250;
const MIN_QUERY_LENGTH = 2;

interface Args {
  searchInputValue: string;
  searchCategory: string;
  selectedViewSet: Set<CalendarTopBarView>;
  dropdownTable: DropdownTable | null;
}

function dedupeCalendarEvents(events: CalendarEvent[]) {
  const eventMap = new Map<string, CalendarEvent>();

  events.forEach((event) => {
    eventMap.set(`${event.logtype}-${event.id}`, event);
  });

  return Array.from(eventMap.values());
}

export function useCalendarSearchAutocomplete({
  searchInputValue,
  searchCategory,
  selectedViewSet,
  dropdownTable,
}: Args): { results: CalendarEvent[]; isLoading: boolean } {
  const [debouncedQuery, setDebouncedQuery] = useState('');

  const debouncedSetRef = useRef(debounce((value: string) => setDebouncedQuery(value), DEBOUNCE_MS));

  useEffect(() => {
    debouncedSetRef.current(searchInputValue);
  }, [searchInputValue]);

  useEffect(() => {
    const fn = debouncedSetRef.current;
    return () => fn.cancel();
  }, []);

  const trimmedQuery = debouncedQuery.trim();
  const isQueryEnabled = trimmedQuery.length >= MIN_QUERY_LENGTH;
  const isServiceEnabled = isQueryEnabled && selectedViewSet.has(TrackerDataType.SERVICE);
  const isMediaEnabled = isQueryEnabled && selectedViewSet.has(TrackerDataType.MEDIA_INTERACTION);
  const isEngagementEnabled = isQueryEnabled && selectedViewSet.has(TrackerDataType.ENGAGEMENT);
  const isContentEnabled = isQueryEnabled && selectedViewSet.has(TrackerDataType.CONTENT_LOG);

  const getCategory = (view: CalendarTopBarView) =>
    searchCategoryByViewAndLabel[view][searchCategory] ?? ALL_FIELDS_SEARCH_CATEGORY;

  const queries = useQueries({
    queries: [
      {
        queryKey: ['calendarAutocompleteServices', trimmedQuery, searchCategory],
        queryFn: () =>
          getCalendarQuickSearchServices({
            queryString: trimmedQuery,
            category: getCategory(TrackerDataType.SERVICE),
          }),
        enabled: isServiceEnabled,
        staleTime: 30_000,
      },
      {
        queryKey: ['calendarAutocompleteMedia', trimmedQuery, searchCategory],
        queryFn: () =>
          getCalendarQuickSearchInteractions({
            queryString: trimmedQuery,
            category: getCategory(TrackerDataType.MEDIA_INTERACTION),
          }),
        enabled: isMediaEnabled,
        staleTime: 30_000,
      },
      {
        queryKey: ['calendarAutocompleteEngagements', trimmedQuery, searchCategory],
        queryFn: () =>
          getCalendarQuickSearchEngagements({
            queryString: trimmedQuery,
            category: getCategory(TrackerDataType.ENGAGEMENT),
          }),
        enabled: isEngagementEnabled,
        staleTime: 30_000,
      },
      {
        queryKey: ['calendarAutocompleteContentLogs', trimmedQuery, searchCategory],
        queryFn: () =>
          getCalendarQuickSearchContentLogs({
            queryString: trimmedQuery,
            category: getCategory(TrackerDataType.CONTENT_LOG),
          }),
        enabled: isContentEnabled,
        staleTime: 30_000,
      },
    ],
  });

  const [serviceQuery, mediaQuery, engagementQuery, contentQuery] = queries;
  const enabledQueries = [
    isServiceEnabled ? serviceQuery : null,
    isMediaEnabled ? mediaQuery : null,
    isEngagementEnabled ? engagementQuery : null,
    isContentEnabled ? contentQuery : null,
  ].filter((q): q is NonNullable<typeof q> => q !== null);
  const isLoading = enabledQueries.some((query) => query.isPending || query.isFetching);
  const areResultsReady = enabledQueries.length > 0 && enabledQueries.every((query) => !query.isPending && !query.isFetching);

  const results = useMemo(() => {
    if (!dropdownTable || !isQueryEnabled || !areResultsReady) return [];

    const allEvents = [
      ...formatCalendarEvents(serviceQuery.data ?? [], dropdownTable, 'entryDate'),
      ...formatCalendarEvents(mediaQuery.data ?? [], dropdownTable, 'entryDate'),
      ...formatCalendarEvents(engagementQuery.data ?? [], dropdownTable, 'entryDate'),
      ...formatCalendarEvents(contentQuery.data ?? [], dropdownTable, 'entryDate'),
    ];

    return dedupeCalendarEvents(allEvents)
      .sort((a, b) => b.calendarDate.getTime() - a.calendarDate.getTime())
      .slice(0, RESULT_CAP);
  }, [serviceQuery.data, mediaQuery.data, engagementQuery.data, contentQuery.data, dropdownTable, isQueryEnabled, areResultsReady]);

  return { results, isLoading };
}
